if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this._initialVariantId = null;

        this._qaTimer = null;
        this._qaObserver = null;
        this._qaBound = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        this._teardownQuickAddGallerySync();

        const cartNotification =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        this.modalContent.innerHTML = '';
        this._initialVariantId = null;

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        this.openedBy = opener;

        const productUrl = opener.getAttribute('data-product-url');
        this._initialVariantId = this._getVariantIdFromUrl(productUrl);

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(productUrl)
          .then((r) => r.text())
          .then(async (htmlText) => {
            const doc = new DOMParser().parseFromString(htmlText, 'text/html');
            const productElement = doc.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            this._selectFirstAvailableVariantInModal();

            if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            await this._primeQuickAddGalleryBeforeShow();

            super.show(opener);

            requestAnimationFrame(() => {
              setTimeout(() => {
                if (window.initGallerySliders) window.initGallerySliders();
              }, 0);
            });

            this._setupQuickAddGallerySync();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((c) => {
          if (c.startsWith('color-') || c === 'gradient') this.modalContent.classList.add(c);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      _selectFirstAvailableVariantInModal() {
        const variantSelects = this.modalContent?.querySelector('variant-selects');
        if (!variantSelects) return;

        const selectedScriptEl = variantSelects.querySelector('[data-selected-variant]');
        const firstAvailableScriptEl = variantSelects.querySelector('[data-first-available-variant]');
        if (!selectedScriptEl?.textContent?.trim()) return;

        let selectedVariant;
        try {
          selectedVariant = JSON.parse(selectedScriptEl.textContent.trim());
        } catch (e) {
          return;
        }
        if (!selectedVariant?.id) return;

        if (selectedVariant.available) return;

        if (!firstAvailableScriptEl?.textContent?.trim()) return;
        let variant;
        try {
          variant = JSON.parse(firstAvailableScriptEl.textContent.trim());
        } catch (e) {
          return;
        }
        if (!variant?.id) return;

        const optionValues = [
          variant.option1 ?? variant.options?.[0],
          variant.option2 ?? variant.options?.[1],
          variant.option3 ?? variant.options?.[2],
        ].filter((v) => v != null && v !== '');

        const norm = (v) => (v == null ? '' : String(v).trim());
        const fieldsets = variantSelects.querySelectorAll('fieldset.js.product-form__input');
        const selects = variantSelects.querySelectorAll('select.select__select');

        fieldsets.forEach((fieldset, index) => {
          const valueToSelect = optionValues[index];
          if (valueToSelect == null) return;
          const valueStr = norm(valueToSelect);
          const radios = fieldset.querySelectorAll('input[type="radio"]');
          for (const radio of radios) {
            const isDisabled = radio.disabled || radio.classList.contains('disabled');
            if (norm(radio.value) === valueStr && !isDisabled) {
              radio.checked = true;
              const legendValue = fieldset.querySelector('[data-selected-value]');
              if (legendValue) legendValue.textContent = valueToSelect;
              break;
            }
          }
        });

        selects.forEach((select, index) => {
          const valueToSelect = optionValues[index];
          if (valueToSelect == null) return;
          const valueStr = norm(valueToSelect);
          const option = Array.from(select.options).find((opt) => norm(opt.value) === valueStr);
          if (option) {
            select.value = option.value;
            select.querySelectorAll('option').forEach((o) => o.removeAttribute('selected'));
            const selectedOpt = select.options[select.selectedIndex];
            if (selectedOpt) selectedOpt.setAttribute('selected', 'selected');
            const swatchEl = select.closest('.product-form__input')?.querySelector('[data-selected-value] .swatch');
            if (swatchEl && selectedOpt?.dataset?.optionSwatchValue) {
              swatchEl.style.setProperty('--swatch--background', selectedOpt.dataset.optionSwatchValue);
              swatchEl.classList.remove('swatch--unavailable');
            }
          }
        });

        const variantInput = this._findVariantIdInput();
        if (variantInput) {
          variantInput.value = String(variant.id);
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const productFormEl = this.modalContent?.querySelector('product-form');
        if (productFormEl?.toggleSubmitButton) {
          const soldOutText = (typeof window.variantStrings !== 'undefined' && window.variantStrings?.soldOut) || 'Sold out';
          productFormEl.toggleSubmitButton(!variant.available, soldOutText);
        }
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        productElement.querySelectorAll('script').forEach((script) => {
          if (script.textContent && script.textContent.includes('window.location.replace') && script.textContent.includes('variant')) script.remove();
        });

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((m) => m.remove());

        const sizeChartSelectors = [
          '[id^="size-chart-"]',
          '.size-chart',
          'size-chart-block',
          '.sc-trigger',
          '[data-sc-modal]',
          '[data-sc-open]',
        ];
        sizeChartSelectors.forEach((sel) => {
          productElement.querySelectorAll(sel).forEach((el) => el.remove());
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        if (!sectionId) return;

        const oldId = sectionId;
        const newId = `${this.id || 'quickadd'}-${sectionId}`;

        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);

        Array.from(productElement.attributes).forEach((attr) => {
          if (typeof attr.value === 'string' && attr.value.includes(oldId)) {
            productElement.setAttribute(attr.name, attr.value.replaceAll(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
        productElement.dataset.section = newId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let sizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) sizes = sizes.replace('715px', '605px');
        else if (product.classList.contains('product--small')) sizes = sizes.replace('715px', '495px');

        mediaImages.forEach((img) => img.setAttribute('sizes', sizes));
      }

     
      _getVariantIdFromUrl(url) {
        try {
          if (!url) return null;
          const u = new URL(url, window.location.origin);
          const v = u.searchParams.get('variant');
          return v ? String(v) : null;
        } catch (e) {
          const match = (url || '').match(/[?&]variant=(\d+)/);
          return match ? String(match[1]) : null;
        }
      }

      _findVariantIdInput() {
        return (
          this.modalContent.querySelector('form[action*="/cart/add"] input[name="id"]') ||
          this.modalContent.querySelector('input[name="id"].product-variant-id') ||
          this.modalContent.querySelector('input[name="id"]')
        );
      }

      _currentVariantId() {
        const input = this._findVariantIdInput();
        const v = input && input.value ? String(input.value) : null;
        return v || this._initialVariantId;
      }

      _thumbUrl(url, width = 200) {
        if (!url) return url;
        if (url.includes('width=')) {
          return url.replace(/width=\d+/g, `width=${width}`);
        }
        const joiner = url.includes('?') ? '&' : '?';
        return `${url}${joiner}width=${width}`;
      }

      _preloadImage(url) {
        return new Promise((resolve) => {
          if (!url) return resolve(false);
          const img = new Image();
          let done = false;

          const finish = (ok) => {
            if (done) return;
            done = true;
            resolve(ok);
          };

          img.onload = () => finish(true);
          img.onerror = () => finish(false);
          img.src = url;

          if (img.decode) {
            img
              .decode()
              .then(() => finish(true))
              .catch(() => finish(false));
          }
        });
      }

      _norm(s) {
        if (!s) return '';
        return s
          .toString()
          .toLowerCase()
          .replace(/[\u200B-\u200D\uFEFF]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      _matchesAlt(alt, color) {
        const imageAlt = this._norm(alt);
        const colorName = this._norm(color);

        if (!imageAlt) return true;
        if (!colorName) return true;

        if (imageAlt.includes(colorName)) return true;

        const parts = imageAlt.split(/[,|/]+/).map((p) => this._norm(p)).filter(Boolean);
        if (parts.includes(colorName)) return true;

        if (parts.some((p) => p.includes(colorName) || colorName.includes(p))) return true;

        if (parts.includes('all') || imageAlt.includes(' all ')) return true;

        return false;
      }

      _getSelectedColor() {
        const modalContent = this.modalContent;
        if (!modalContent) return null;

        const vs = modalContent.querySelector('variant-selects');
        if (!vs) return null;

        // radio fieldsets
        for (const fs of vs.querySelectorAll('fieldset')) {
          const legend = this._norm(fs.querySelector('legend')?.textContent || '');
          if (legend.includes('color') || legend.includes('colour')) {
            const checked = fs.querySelector('input[type="radio"]:checked');
            if (checked?.value) return checked.value;
          }
        }

        // selects
        for (const sel of vs.querySelectorAll('select')) {
          const label = this._norm(sel.closest('.product-form__input')?.querySelector('label')?.textContent || '');
          if (label.includes('color') || label.includes('colour')) {
            const value = sel.value || sel.options[sel.selectedIndex]?.text;
            if (value) return value;
          }
        }

        return null;
      }

      _collectAltMatchedImageUrls() {
        const mediaGallery = this.modalContent.querySelector('media-gallery');
        if (!mediaGallery) return [];

        const color = this._getSelectedColor();

        // main slides only (avoid pulling thumb images)
        const items = Array.from(
          mediaGallery.querySelectorAll('[id^="Slider-Gallery-"] .product__media-item, .product__media-item')
        );

        const urls = [];
        const seen = new Set();

        for (const item of items) {
          const img = item.querySelector('img');
          if (!img) continue;

          const alt = item.getAttribute('data-alt-text') || img.getAttribute('alt') || '';
          if (!this._matchesAlt(alt, color)) continue;

          const src = img.currentSrc || img.getAttribute('src') || '';
          if (!src) continue;

          const key = src.replace(/([?&])width=\d+/g, '$1').replace(/[?&]$/, '');
          if (seen.has(key)) continue;
          seen.add(key);

          urls.push(src);
        }

        // fallback: show all if none matched
        if (!urls.length) {
          for (const item of items) {
            const img = item.querySelector('img');
            if (!img) continue;
            const src = img.currentSrc || img.getAttribute('src') || '';
            if (!src) continue;
            const key = src.replace(/([?&])width=\d+/g, '$1').replace(/[?&]$/, '');
            if (seen.has(key)) continue;
            seen.add(key);
            urls.push(src);
          }
        }

        return urls.filter(Boolean);
      }

      async _primeQuickAddGalleryBeforeShow() {
        // allow product-info to connect and set initial variant selection
        await new Promise((r) => requestAnimationFrame(r));

        const urls = this._collectAltMatchedImageUrls();
        if (!urls.length) {
          this._unmountQuickAddGallery();
          return;
        }

        // preload first main image (large)
        const firstMain = this._thumbUrl(urls[0], 1000);
        try {
          await this._preloadImage(firstMain);
        } catch (e) {}

        this._mountQuickAddGallery(urls);
      }

      _setupQuickAddGallerySync() {
        this._teardownQuickAddGallerySync();

        const handler = () => {
          clearTimeout(this._qaTimer);
          this._qaTimer = setTimeout(() => {
            const urls = this._collectAltMatchedImageUrls();
            if (urls.length) this._mountQuickAddGallery(urls);
            else this._unmountQuickAddGallery();
          }, 0);
        };
        this._qaBound = handler;

        // capture changes from variant selectors
        this.modalContent.addEventListener('change', handler, true);

        // watch hidden variant id input updates
        const input = this._findVariantIdInput();
        if (input) {
          this._qaObserver = new MutationObserver(handler);
          this._qaObserver.observe(input, { attributes: true, attributeFilter: ['value'] });
        }

        // initial apply
        handler();
      }

      _teardownQuickAddGallerySync() {
        clearTimeout(this._qaTimer);
        this._qaTimer = null;

        if (this._qaBound) {
          this.modalContent.removeEventListener('change', this._qaBound, true);
          this._qaBound = null;
        }

        if (this._qaObserver) {
          this._qaObserver.disconnect();
          this._qaObserver = null;
        }

        this._unmountQuickAddGallery();
      }

      _mountQuickAddGallery(urls) {
        const mediaGallery = this.modalContent.querySelector('media-gallery');
        if (!mediaGallery) return;
      
        mediaGallery.classList.add('dcoded-qa-active');
      
        let container = mediaGallery.querySelector('[data-dcoded-qa-gallery]');
        if (!container) {
          container = document.createElement('div');
          container.setAttribute('data-dcoded-qa-gallery', 'true');
          container.className = 'dcoded-qa-gallery';
          mediaGallery.prepend(container);
        }
      
        const safe = (urls || []).filter(Boolean);
        if (!safe.length) return;
      
        let index = 0;
      
        const clamp = (n) => Math.max(0, Math.min(n, safe.length - 1));
      
        // Build thumbs as DIVs
        let thumbsHTML = '';
        if (safe.length > 1) {
          thumbsHTML =
            '<div class="dcoded-qa-thumbs">' +
            safe
              .map((u, idx) => {
                const active = idx === 0 ? ' is-active' : '';
                const thumbSrc = this._thumbUrl(u, 160);
                return `
                  <div
                    class="dcoded-qa-thumb${active}"
                    data-index="${idx}"
                    role="button"
                    tabindex="0"
                    aria-label="Image ${idx + 1}"
                  >
                    <img src="${thumbSrc}" alt="" loading="eager" decoding="async" width="60" height="60" draggable="false">
                  </div>
                `;
              })
              .join('') +
            '</div>';
        }
      
        const firstMain = this._thumbUrl(safe[0], 1000);
      
        container.innerHTML = `
          <div class="dcoded-qa-main" data-dcoded-qa-mainwrap>
            <button type="button" class="dcoded-qa-nav dcoded-qa-nav--prev" aria-label="Previous image">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M15 6 L9 12 L15 18" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
      
            <img data-dcoded-qa-main src="${firstMain}" alt="" loading="eager" fetchpriority="high" decoding="async" draggable="false">
      
            <button type="button" class="dcoded-qa-nav dcoded-qa-nav--next" aria-label="Next image">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M9 6 L15 12 L9 18" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          ${thumbsHTML}
        `;
      
        const mainWrap = container.querySelector('[data-dcoded-qa-mainwrap]');
        const main = container.querySelector('[data-dcoded-qa-main]');
        const prevBtn = container.querySelector('.dcoded-qa-nav--prev');
        const nextBtn = container.querySelector('.dcoded-qa-nav--next');
        const thumbs = Array.from(container.querySelectorAll('.dcoded-qa-thumb'));
      
        // Warm main images
        safe.forEach((u) => {
          const warm = new Image();
          warm.decoding = 'async';
          warm.src = this._thumbUrl(u, 1000);
        });
      
        const setNavState = () => {
          if (!prevBtn || !nextBtn) return;
      
          const isSingle = safe.length <= 1;
          prevBtn.style.display = isSingle ? 'none' : '';
          nextBtn.style.display = isSingle ? 'none' : '';
      
          const prevDisabled = index === 0;
          const nextDisabled = index === safe.length - 1;
      
          prevBtn.disabled = prevDisabled;
          nextBtn.disabled = nextDisabled;
      
          prevBtn.classList.toggle('is-disabled', prevDisabled);
          nextBtn.classList.toggle('is-disabled', nextDisabled);
        };
      
        const setActiveThumb = () => {
          thumbs.forEach((t) => t.classList.remove('is-active'));
          const active = thumbs.find((t) => Number(t.dataset.index) === index);
          if (active) active.classList.add('is-active');
        };
      
        const goTo = (i) => {
          index = clamp(i);
          const next = this._thumbUrl(safe[index] || safe[0], 1000);
          if (main) main.src = next;
          setActiveThumb();
          setNavState();
        };
      
        // Thumb click + keyboard support
        const bindThumb = (el) => {
          const run = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const idx = Number(el.dataset.index || '0');
            goTo(idx);
          };
      
          el.addEventListener('click', run);
          el.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') run(e);
          });
        };
      
        thumbs.forEach(bindThumb);
      
        // Prev/Next click
        const bindNav = (btn, dir) => {
          if (!btn) return;
      
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (btn.disabled) return;
            goTo(index + dir);
          });
      
          btn.addEventListener('pointerdown', (e) => e.stopPropagation());
        };
      
        bindNav(prevBtn, -1);
        bindNav(nextBtn, +1);
      
       
        if (mainWrap) {
          let startX = 0;
          let startY = 0;
          let dx = 0;
          let dy = 0;
          let isDown = false;
          let isSwiping = false;
          let startTime = 0;
      
          const THRESHOLD = 40;     // swipe distance
          const RESTRAINT = 80;     // max vertical drift
          const ALLOWED_TIME = 450; // ms
      
          const onDown = (e) => {
            // only primary button / touch
            if (e.pointerType === 'mouse' && e.button !== 0) return;
      
            isDown = true;
            isSwiping = false;
            dx = 0;
            dy = 0;
      
            startX = e.clientX;
            startY = e.clientY;
            startTime = Date.now();
      
            // don't let modal treat as click-outside etc
            e.stopPropagation();
          };
      
          const onMove = (e) => {
            if (!isDown) return;
      
            dx = e.clientX - startX;
            dy = e.clientY - startY;
      
            // decide if this is a horizontal swipe or vertical scroll
            if (!isSwiping) {
              if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
                isSwiping = true;
                // capture pointer so we keep receiving moves
                try { mainWrap.setPointerCapture(e.pointerId); } catch (_) {}
              } else if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
                // user is scrolling vertically; abort swipe
                isDown = false;
                isSwiping = false;
                return;
              }
            }
      
            if (isSwiping) {
              // prevent horizontal page swipe/back gestures
              e.preventDefault();
              e.stopPropagation();
            }
          };
      
          const onUp = (e) => {
            if (!isDown) return;
            isDown = false;
      
            if (!isSwiping) return;
      
            const elapsed = Date.now() - startTime;
      
            // release capture
            try { mainWrap.releasePointerCapture(e.pointerId); } catch (_) {}
      
            if (elapsed <= ALLOWED_TIME && Math.abs(dx) >= THRESHOLD && Math.abs(dy) <= RESTRAINT) {
              if (dx < 0) {
                // swipe left -> next
                goTo(index + 1);
              } else {
                // swipe right -> prev
                goTo(index - 1);
              }
            }
      
            isSwiping = false;
          };
      
          mainWrap.addEventListener('pointerdown', onDown, { passive: true });
          mainWrap.addEventListener('pointermove', onMove, { passive: false });
          mainWrap.addEventListener('pointerup', onUp, { passive: true });
          mainWrap.addEventListener('pointercancel', onUp, { passive: true });
          mainWrap.addEventListener('lostpointercapture', onUp, { passive: true });
        }
      
        // Init
        goTo(0);
      }
      
      
      _unmountQuickAddGallery() {
        const mediaGallery = this.modalContent.querySelector('media-gallery');
        if (!mediaGallery) return;

        mediaGallery.classList.remove('dcoded-qa-active');

        const container = mediaGallery.querySelector('[data-dcoded-qa-gallery]');
        if (container) container.remove();
      }
    }
  );
}
